<template>
    <DividerTitle :title="title" />
    <v-row class="d-flex justify-start">
        <!-- Country Selection -->
        <v-col
            cols="12"
            :sm="useAsFilter ? 6 : 3"
            :md="useAsFilter ? 6 : 4"
            :lg="useAsFilter ? 6 : 3"
        >
            <v-autocomplete
                ref="country"
                :data-cy="`${dataCyPrefix}-country`"
                v-model="localLocation.country"
                :items="filteredCountries"
                item-title="name"
                item-value="id"
                :label="$t('formFields.country')"
                @update:modelValue="handleCountrySelection"
                v-auto-animate
                :rules="[requiredRule]"
                hide-details="auto"
            ></v-autocomplete>
        </v-col>

        <!-- City Selection -->
        <v-col
            cols="12"
            :sm="useAsFilter ? 6 : 3"
            :md="useAsFilter ? 6 : 5"
            :lg="useAsFilter ? 6 : 3"
        >
            <v-autocomplete
                ref="city"
                :data-cy="`${dataCyPrefix}-city`"
                v-model="localLocation.city"
                v-model:search="citySearch"
                :items="filteredCities"
                item-title="displayName"
                item-value="id"
                :label="$t('formFields.city')"
                :loading="isFetchingCities"
                variant="outlined"
                v-auto-animate
                :rules="[requiredRule]"
                hide-details="auto"
                no-filter
                return-object
                :disabled="!localLocation.country"
                :no-data-text="
                    (citySearch || '').length < 3
                        ? $t('addPaymentIncident.typeThreeChars')
                        : $t('addPaymentIncident.noResults')
                "
            >
                <!-- addNewCity trigger when no exact match -->
                <template #append-item>
                    <v-list-item
                        v-if="
                            citySearch.length >= 3 &&
                            !filteredCities.some(
                                (c) => c.displayName === citySearch.trim()
                            )
                        "
                        @click="addNewCity"
                    >
                        <v-list-item-title>
                            <v-btn>
                            {{ $t("formFields.addCity", { name: citySearch }) }}
                        </v-btn>
                        </v-list-item-title>
                    </v-list-item>
                </template>
            </v-autocomplete>
        </v-col>

        <!-- Postal Code -->
        <v-col v-if="!useAsFilter" sm="3" md="3" xs="12" lg="2">
            <v-text-field
                ref="postalCode"
                :data-cy="`${dataCyPrefix}-postal-code`"
                v-model="localLocation.postalCode"
                :label="$t('formFields.postalCode')"
                v-auto-animate
                :rules="[requiredRule]"
                hide-details="auto"
            />
        </v-col>

        <!-- Date & Time Selection (FormDateTime remains outside v-col) -->
        <FormDateTime
            v-if="!useAsFilter"
            ref="dateTime"
            :formType="formType"
            v-model="localLocation"
            :data-cy-prefix="dataCyPrefix"
        />
    </v-row>
</template>

<script>
import FormDateTime from "./FormDateTime.vue";
import { getCityLookupBatchByCountry } from "@/api.js";
import DividerTitle from "./DividerTitle.vue";
import { mapActions, mapState } from "vuex";
import emitter from "@/eventBus";
import { showAlertModal } from "@/utils";
import axios from "axios";

export default {
    components: { FormDateTime, DividerTitle },

    /**
     * Component properties
     * @property {Object} modelValue - The v-model object containing location details.
     * @property {String} formType - The type of form (either "truck" or "ware").
     * @property {String} title - The title of the form section.
     */
    props: {
        modelValue: Object,
        formType: String,
        title: String,
        useAsFilter: Boolean,
        dataCyPrefix: String,
    },

    /**
     * Events emitted by the component
     * @event update:modelValue - Emits the updated location object.
     */
    emits: ["update:modelValue"],

    /**
     * Component's local reactive data.
     * @returns {Object}
     */
    data() {
        return {
            isFetchingCities: false,
            citySearch: "",
            userAddedCities: [],
            combinedData: [],
            /**
             * Stores the selected location details.
             * Includes country, city, postal code, and date & time.
             * @type {Object}
             */
            localLocation: {
                country: "",
                city: "",
                postalCode: "",
                date: [],
                dateStart: null,
                dateEnd: null,
                timeStart: null,
                timeEnd: null,
                ...this.modelValue,
            },
            cityLookupByCountry: [],
            requiredRule: (value) => !!value || this.$t("validation.required"),
        };
    },

    computed: {
        ...mapState(["filteredCountries", "selectedLanguage"]),
        /**
         * Formats cities to include their region and subregion.
         * @returns {Array<Object>} - Array of formatted city names with region/subregion info.
         */
        formattedCities() {
            if (
                !this.localLocation.country ||
                !this.cityLookupByCountry[this.localLocation.country]
            ) {
                return [];
            }

            const cities = Object.keys(
                this.cityLookupByCountry[this.localLocation.country]
            ).map((cityId) => ({
                id: cityId,
                displayName:
                    this.cityLookupByCountry[this.localLocation.country][
                        cityId
                    ],
            }));
            return cities;
        },

        // Computed property for local filtering
        filteredCities() {
            if (this.citySearch.length < 3) {
                return [];
            }

            const searchTerm = this.removeAccents(
                this.citySearch
            ).toLowerCase();

            const added = this.userAddedCities
                .filter((c) => c.country === this.localLocation.country)
                .filter((c) =>
                    this.removeAccents(c.displayName)
                        .toLowerCase()
                        .includes(searchTerm)
                );

            const finalCities = this.formattedCities.filter((city) => {
                const normalizedCity = this.removeAccents(
                    city.displayName
                ).toLowerCase();
                return normalizedCity.includes(searchTerm);
            });

            const allMatches = [...added, ...finalCities];
            const prefixMatches = allMatches.filter((c) =>
                this.removeAccents(c.displayName)
                    .toLowerCase()
                    .startsWith(searchTerm)
            );
            const otherMatches = allMatches.filter(
                (c) =>
                    !this.removeAccents(c.displayName)
                        .toLowerCase()
                        .startsWith(searchTerm)
            );

            return [...prefixMatches, ...otherMatches];
        },
    },
    async mounted() {
        await this.fetchFilteredCountries();
        // Set up a one-time watcher on localLocation.country
        const unwatch = this.$watch(
            "localLocation.country",
            async (newCountry) => {
                if (newCountry && this.citySearch.length >= 3) {
                    await this.fetchCityLookup();
                    unwatch();
                }
            },
            { immediate: true }
        );

        emitter.on("language-changed", async (newLang) => {
            this.$store.state.selectedLanguage = newLang;
            await this.fetchFilteredCountries();
            await this.fetchCityLookup();
        });
    },
    unmounted() {
        emitter.off("language-changed");
    },
    methods: {
        ...mapActions(["fetchFilteredCountries"]),
        showAlertModal,
        /**
         * Removes diacritical marks (accents) from the given string.
         *
         * @param {string} str - The input string that may contain accented characters.
         * @returns {string} A normalized string with the accents removed.
         */
        removeAccents(str) {
            return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        },
        /**
         * Adds a new city to the userAddedCities list and selects it.
         *
         * Trims the current citySearch string; if non-empty, constructs a new
         * city object (using the name as its ID and displayName), pushes it
         * onto userAddedCities, sets it as the selected city, clears the
         * search, and emits the updated location.
         *
         * @returns {void}
         */
        addNewCity() {
            const name = this.citySearch.trim();
            if (!name) return;
            const newCity = {
                id: name, // we'll keep the name as key
                displayName: name,
                country: this.localLocation.country,
                userAdded: true,
            };
            this.userAddedCities.push(newCity);
            this.localLocation.city = newCity;
            this.citySearch = name;
            this.emitUpdate();
        },
        /**
         * Fetches manual (user-added) cities from the server for the current country & search term.
         *
         * Makes a GET request to `/user-added-cities/` with `country` and `search` params.
         * On success, normalizes the returned array into `{ id, displayName, country }`
         * objects and replaces `userAddedCities`. Errors are logged to the console.
         *
         * @async
         * @returns {Promise<void>}
         */
        async fetchUserAddedCities() {
            if (!this.localLocation.country) return;
            try {
                const { data } = await axios.get("/user-added-cities/", {
                    params: {
                        country: this.localLocation.country,
                        search: this.citySearch,
                    },
                });
                // normalize into the same shape as API cities:
                this.userAddedCities = data.map((c) => ({
                    id: c.name,
                    displayName: c.name,
                    country: c.country,
                }));
            } catch (e) {
                console.error("couldn’t load manual cities", e);
            }
        },
        /**
         * Asynchronously fetches the city lookup data for the selected country and search term.
         *
         * This method calls the API to retrieve alternative city names based on the
         * selected country and current city search query, then updates the component's
         * cityLookupByCountry data. It also attempts to rebind the selected city object
         * (if already chosen) with its updated display name from the new lookup.
         *
         * @returns {Promise<void>} A promise that resolves when the lookup and update are complete.
         */
        async fetchCityLookup() {
            this.isFetchingCities = true;
            try {
                this.cityLookupByCountry = await getCityLookupBatchByCountry(
                    [this.localLocation.country],
                    this.selectedLanguage,
                    this.citySearch
                );
                // also pull any manual cities for this country+search
                await this.fetchUserAddedCities();

                // Sync city object after fetching
                const matched = this.formattedCities.find(
                    (city) => city.id == this.localLocation.city?.id
                );
                if (matched) {
                    this.localLocation.city = matched; // Rebind new object with updated displayName
                }
            } catch (error) {
                console.error("Error fetching city lookup:", error);
                this.showAlertModal(
                    this.$store,
                    "Error fetching city lookup",
                    "danger",
                    12000,
                    error
                );
            } finally {
                this.isFetchingCities = false;
            }
        },
        /**
         * Validates all address form fields in the component.
         *
         * This method triggers validation on country, city, postal code, and date/time fields,
         * and accumulates their validation statuses. It returns a boolean indicating whether all
         * fields pass their validation rules.
         *
         * @returns {Promise<boolean>} A promise that resolves to true if all fields are valid; otherwise, false.
         */
        async validateFields() {
            await this.$nextTick();

            let isValid = true;

            if (this.$refs.country) {
                const valid = await this.$refs.country.validate();

                if (this.$refs.country.errorMessages.length > 0)
                    isValid = false;
            }

            if (this.$refs.city) {
                const valid = await this.$refs.city.validate();

                if (this.$refs.city.errorMessages.length > 0) isValid = false;
            }

            if (this.$refs.postalCode) {
                const valid = await this.$refs.postalCode.validate();

                if (this.$refs.postalCode.errorMessages.length > 0)
                    isValid = false;
            }

            return isValid;
        },
        /**
         * Resets validation errors on all form-related components.
         *
         * This method checks for the presence of form field references (country, city, postalCode, dateTime)
         * and calls their respective resetValidation() functions to clear any current validation error messages.
         */
        resetValidation() {
            if (this.$refs.country) this.$refs.country.resetValidation();
            if (this.$refs.city) this.$refs.city.resetValidation();
            if (this.$refs.postalCode) this.$refs.postalCode.resetValidation();
            if (this.$refs.dateTime) this.$refs.dateTime.resetValidation();
        },
        /**
         * Handles changes in the country selection.
         *
         * When a new country is selected, this method updates the localLocation.country value,
         * resets the city selection to ensure no stale city data persists, and clears the stored
         * list of cities (combinedData) for the previously selected country.
         *
         * @param {string} selectedCountry - The identifier (usually an ID) of the newly selected country.
         */
        // When a country is selected, update the country and clear combinedData
        async handleCountrySelection(selectedCountry) {
            this.localLocation.country = selectedCountry;
            this.localLocation.city = "";
            this.combinedData = [];
        },
        /**
         * Emits the updated location object to the parent component.
         *
         * This method compiles the current localLocation data (including country, city, postal code,
         * and date/time fields) into an object and emits it via the 'update:modelValue' event,
         * ensuring that parent components stay in sync with local form data.
         */
        emitUpdate() {
            this.$emit("update:modelValue", {
                ...this.localLocation,
                city: this.localLocation.city || "",
                date: this.localLocation.date,
                dateStart: this.localLocation.dateStart,
                dateEnd: this.localLocation.dateEnd,
            });
        },
    },
    watch: {
        citySearch(newVal) {
            if (newVal.length > 2) {
                this.fetchCityLookup();
                this.fetchUserAddedCities();
            }
        },
        /**
         * Watches for deep changes in `localLocation` and emits updates.
         */
        localLocation: {
            handler(newVal) {
                this.emitUpdate();
            },
            deep: true,
        },

        /**
         * Watches for external changes in `modelValue` and updates local state accordingly.
         */
        modelValue: {
            handler(newVal) {
                if (
                    JSON.stringify(newVal) !==
                    JSON.stringify(this.localLocation)
                ) {
                    this.localLocation = {
                        ...newVal,
                        date: [...(newVal.date || [])],
                    };
                }
            },
            deep: true,
        },
        selectedLanguage(newLang) {
            this.fetchCityLookup(); // Fetch new city names
        },
    },
};
</script>
